import { gql } from "@apollo/client";
import client from "../apolloClient";
import {
  Screen,
  CreateScreenInput,
  UpdateScreenInput,
  ScreenResult,
  CityWithRegions,
  ScreenColumnFilter,
  ScreenFilterField,
  ScreenSortOrder,
} from "../../schemas/screenSchema";

export interface ScreenFilters {
  search?: string;
  page?: number;
  pageSize?: number;
  regionId?: string;
  tagType?: string;
  status?: boolean;
  orientation?: string;
  columnFilters?: ScreenColumnFilter[];
  sortBy?: ScreenFilterField;
  sortOrder?: ScreenSortOrder;
}

export interface UpdateScreenPayload {
  id: string;
  screen: UpdateScreenInput;
}

export interface ScreensResponse {
  data: Screen[];
  total?: number;
  page?: number;
  limit?: number;
}

const GET_SCREENS = gql`
  query GetScreens($page: Int, $pageSize: Int, $search: String, $regionId: ID, $tagType: String, $status: Boolean, $orientation: String, $columnFilters: [ScreenColumnFilter!], $sortBy: ScreenFilterField, $sortOrder: ScreenSortOrder) {
    screens(
      page: $page
      pageSize: $pageSize
      search: $search
      regionId: $regionId
      tagType: $tagType
      status: $status
      orientation: $orientation
      columnFilters: $columnFilters
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      screens {
        id
        screenName
        debugFlag
        resetFlag
        lastResetAt
        status
        lastOnAt
        layout
        screenSaver
        address
        latitude
        longitude
        regionId
        region {
          id
          name
          city {
            id
            name
          }
        }
        restaurantId
        restaurant {
          id
          code
          name
          description
        }
        clientPlaylist {
          id
          name
          type
          duration
          isActive
          tenantId
          createdBy
          createdAt
          updatedAt
        }
        tenantId
        tickerId
        widgetId
        primeEndTime
        primeStartTime
        orientation
        createdBy
        createdAt
        updatedAt
        tags
        tagIds
        clients {
          id
          name
        }
        isScheduled
        device {
          id
          isPi
        }
        verificationCode
        anydeskId
      }
      page
      pageSize
      total
      totalPages
      hasNextPage
      hasPrevPage
    }
  }
`;

const GET_SCREEN_BY_ID = gql`
  query GetScreenById($id: ID!) {
    screen(id: $id) {
      id
      screenName
      debugFlag
      resetFlag
      lastResetAt
      status
      lastOnAt
      layout
      screenSaver
      address
      latitude
      longitude
      regionId
      region {
        id
        name
        city {
          id
          name
        }
      }
      restaurantId
      restaurant {
        id
        code
        name
        description
      }
      clientPlaylist {
        id
        name
        type
        duration
        isActive
        tenantId
        createdBy
        createdAt
        updatedAt
      }
      tenantId
      tickerId
      widgetId
      primeEndTime
      primeStartTime
      orientation
      createdBy
      createdAt
      updatedAt
      tags
      tagIds
      clients {
        id
        name
      }
      isScheduled
      device {
        id
        isPi
      }
      verificationCode
      anydeskId
    }
  }
`;

const GET_SCREENS_BY_CITY_WITH_REGIONS = gql`
  query GetScreensByCityWithRegions($search: String, $orientation: String) {
    screensByCityWithRegions(search: $search, orientation: $orientation) {
      city
      regions {
        regionName
        screens {
          screenId
          screenName
        }
      }
    }
  }
`;

const CREATE_SCREEN = gql`
  mutation CreateScreen($input: CreateScreenInput!) {
    createScreen(input: $input)
  }
`;

const UPDATE_SCREEN = gql`
  mutation UpdateScreen($id: ID!, $input: UpdateScreenInput!) {
    updateScreen(id: $id, input: $input)
  }
`;

const RESET_SCREEN = gql`
  mutation ResetScreen($id: ID!) {
    resetScreen(id: $id)
  }
`;

const DELETE_SCREENS = gql`
  mutation DeleteScreens($ids: [ID!]!) {
    deleteScreens(ids: $ids)
  }
`;

export const NOW_PLAYING_CHANGED = gql`
  subscription NowPlayingChanged($screenId: ID!) {
    nowPlayingChanged(screenId: $screenId) {
      screenId
      creativeId
      creativeName
      creativeType
      fileUrl
      duration
      startedAt
    }
  }
`;

const GET_NOW_PLAYING = gql`
  query GetNowPlaying($id: ID!) {
    screen(id: $id) {
      nowPlayingCreativeId
      nowPlayingCreativeName
      nowPlayingType
      nowPlayingStartedAt
      nowPlayingDuration
      nowPlayingFileUrl
    }
  }
`;

export const SCREEN_STATUS_CHANGED = gql`
  subscription ScreenStatusChanged {
    screenStatusChanged {
      screenId
      screenName
      isOn
      changedAt
    }
  }
`;

export const screenService = {
  getAll: async (filters: ScreenFilters = {}): Promise<ScreenResult> => {
    const { data } = await client.query({
      query: GET_SCREENS,
      variables: filters,
      fetchPolicy: "network-only",
    });
    return data.screens;
  },

  getById: async (id: string): Promise<Screen> => {
    const { data } = await client.query({
      query: GET_SCREEN_BY_ID,
      variables: { id },
      fetchPolicy: "network-only",
    });
    return data.screen;
  },

  getScreensByCityWithRegions: async (filters?: {
    search?: string;
    orientation?: string;
  }): Promise<CityWithRegions[]> => {
    const { data } = await client.query({
      query: GET_SCREENS_BY_CITY_WITH_REGIONS,
      variables: {
        search: filters?.search || undefined,
        orientation: filters?.orientation || undefined,
      },
      fetchPolicy: "network-only",
    });
    return data.screensByCityWithRegions;
  },

  create: async (input: CreateScreenInput): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: CREATE_SCREEN,
      variables: { input },
    });
    return data.createScreen;
  },

  update: async (payload: UpdateScreenPayload): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: UPDATE_SCREEN,
      variables: { id: payload.id, input: payload.screen },
    });
    return data.updateScreen;
  },

  reset: async (id: string): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: RESET_SCREEN,
      variables: { id },
    });
    return data.resetScreen;
  },

  remove: async (ids: string[]): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: DELETE_SCREENS,
      variables: { ids },
    });
    return data.deleteScreens;
  },

  getNowPlaying: async (id: string) => {
    const { data } = await client.query({
      query: GET_NOW_PLAYING,
      variables: { id },
      fetchPolicy: "network-only",
    });
    return data.screen;
  },
};
